import Vue from "vue";
import { AbacoWebModule, RouterReadyEvent, VueRouter, ApplicationReadyEvent, MenuItem } from '@abaco/web-common/src/app';
import globals from "./globals";
import myRoutes from "./routes";
import TreatmentVuexModule from './state';
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import { createMenu } from './menu';
import TreatmentApi from './api/TreatmentApi';
import MenuModule, { MODULE_ID as MENU_MODULE_ID } from '@abaco/menu-module/src/modules/menu';
import GlobalStateModule from '@abaco/portal-global-state/src/modules/global-state';
import FarmerRegistryModule from '@abaco/farm-registry/src/modules/farm-registry';
import CropPlanModule from "@abaco/crop-plan-module/src/modules/crop-plan-module/index";
import { UnitOfMeasure } from './models/ActivityExecution';
import FertilizerApi from './api/FertilizerApi';
import WaterApi from './api/WaterApi';
import TreatmentPlotApi from "./api/TreatmentPlotApi";
import WeatherApi from "./api/WeatherApi";
import PhytochemicalApi from "./api/PhytochemicalApi";
import ActivityApi from "./api/ActivityApi";
import PrintApi from "./api/PrintApi";
import EquipmentApi from "./api/EquipmentApi";
import IrrigationApi from './api/IrrigationApi';
import UnitOfMeasureApi from "./api/UnitsOfMeasureApi";
import WarehouseApi from "./api/WarehouseApi";
import ChartApi from "./api/ChartApi";
import DashboardModule from '@abaco/dashboard-module/src/modules/dashboard';
import DashboardChartsModule from '@abaco/dashboard-module/src/modules/charts';
import SeedApi from "./api/SeedApi";
import ExemptionApi from "./api/ExemptionApi";

export const MODULE_ID = "treatment_register";
const state = new TreatmentVuexModule();

const sitiAgriRestApi = "/sitiagri-rest-api/api_basic";
const exemptionRootPath = "/prescription-registry-api/api";
const treatmentApiRoot = "/treatment-register-api/api";

export default class TreatmentModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;
    private treatmentApi!: TreatmentApi;
    private fertApi!: FertilizerApi;
    private watApi!: WaterApi;
    private weathApi!: WeatherApi;
    private prtApi!: PrintApi;
    private apiCreated = false;
    private treatmentPlotApi!: TreatmentPlotApi;
    private router!: VueRouter;
    private http!: HTTPClient;
    private phytoApi!: PhytochemicalApi;
    private actApi!: ActivityApi;
    private eqpApi!: EquipmentApi;
    private irrApi!: IrrigationApi;
    private uomApi!: UnitOfMeasureApi;
    private warApi!: WarehouseApi;
    private chartApi!: ChartApi;
    private seeApi!: SeedApi;
    private exptApi!: ExemptionApi;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = globals;
    readonly vuexModule = state;
    menu: MenuItem[] = [];

    dependsOn = {
        [MENU_MODULE_ID]: () => new MenuModule(),
        [FarmerRegistryModule.MODULE_ID]: () => new FarmerRegistryModule(),
        [GlobalStateModule.MODULE_ID]: () => new GlobalStateModule(),
        [CropPlanModule.MODULE_ID]: () => new CropPlanModule(),
        [DashboardModule.MODULE_ID]: () => new DashboardModule(),
        [DashboardChartsModule.MODULE_ID]: () => new DashboardChartsModule(),
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.router = evt.router;
        this.menu = createMenu(this.router);
        Vue.filter("only4Digits", (val: number, additionalText: string) => { return `${Number(val).toLocaleString(undefined, {minimumFractionDigits: 4, maximumFractionDigits: 4})} ${additionalText}`});
        Vue.filter("onlyNDigits", (val: number, n: number) => { return `${Number(val).toLocaleString(undefined, {minimumFractionDigits: n, maximumFractionDigits: n})}`});
    }

    onApplicationReady(evt: ApplicationReadyEvent) {
        this.http = evt.http;
    }

    async beforeEnterAnyRoutes(http: HTTPClient){
        if(this.apiCreated){
            return;
        }
        this.apiCreated = true;

        const TreatmentApi = (await import("./api/TreatmentApi")).default;
        this.treatmentApi = new TreatmentApi(http, treatmentApiRoot, sitiAgriRestApi);

        const FertilizerApi = (await import("./api/FertilizerApi")).default;
        this.fertApi = new FertilizerApi(http, sitiAgriRestApi);

        const WaterApi = (await import("./api/WaterApi")).default;
        this.watApi = new WaterApi(http, sitiAgriRestApi);

        const TreatmentPlotApi = (await import("./api/TreatmentPlotApi")).default;
        this.treatmentPlotApi = new TreatmentPlotApi(http, sitiAgriRestApi, this.vuexModule);

        const WeatherApi = (await import("./api/WeatherApi")).default;
        this.weathApi = new WeatherApi(http, sitiAgriRestApi);

        const PhytochemicalApi = (await import("./api/PhytochemicalApi")).default;
        this.phytoApi = new PhytochemicalApi(http, sitiAgriRestApi);

        const ActivityApi = (await import("./api/ActivityApi")).default;
        this.actApi = new ActivityApi(http);

        const PrintApi = (await import("./api/PrintApi")).default;
        this.prtApi = new PrintApi(http, treatmentApiRoot);

        const EquipmentApi = (await import("./api/EquipmentApi")).default;
        this.eqpApi = new EquipmentApi(http, sitiAgriRestApi);

        const IrrigationApi = (await import("./api/IrrigationApi")).default;
        this.irrApi = new IrrigationApi(http, sitiAgriRestApi);

        const UnitOfMeasureApi = (await import("./api/UnitsOfMeasureApi")).default;
        this.uomApi = new UnitOfMeasureApi(http, sitiAgriRestApi);

        const WarehouseApi = (await import("./api/WarehouseApi")).default;
        this.warApi = new WarehouseApi(http, sitiAgriRestApi);

        const ChartApi = (await import("./api/ChartApi")).default;
        this.chartApi = new ChartApi(http, treatmentApiRoot);

        const SeedApi = (await import("./api/SeedApi")).default;
        this.seeApi = new SeedApi(http, sitiAgriRestApi);

        const ExemptionApi = (await import("./api/ExemptionApi")).default;
        this.exptApi = new ExemptionApi(http, exemptionRootPath, treatmentApiRoot);
    }

    public get TreatmentApi(){
        return this.treatmentApi;
    }

    public get fertilizerApi(){
        return this.fertApi;
    }

    public get waterApi(){
        return this.watApi;
    }

    public get plotApi(){
        return this.treatmentPlotApi;
    }

    public get weatherApi() {
        return this.weathApi;
    }

    public get phytoChemicalApi() {
        return this.phytoApi;
    }

    public get activityApi() {
        return this.actApi;
    }
    public get printApi() {
        return this.prtApi;
    }

    public get equipmentApi() {
        return this.eqpApi;
    }

    public get warehouseApi() {
        return this.warApi;
    }

    get irrigationApi() {
        return this.irrApi;
    }

    get unitOfMeasureApi() {
        return this.uomApi;
    }

    public get ChartApi(){
        return this.chartApi;
    }

    get seedApi() {
        return this.seeApi;
    }

    get exeptionApi(){
        return this.exptApi;
    }

    public async updateUoms(family_name?: string, decodes = false): Promise<UnitOfMeasure[] | null> {
        if (!family_name) {
            return null;
        }
        if (!this.vuexModule.unitOfMeasures[family_name]) {
            const res = decodes ? await this.TreatmentApi.getDecodesUoms(family_name) : await this.unitOfMeasureApi.getWarehouseUoms(family_name);
            this.vuexModule.unitOfMeasures[family_name] = res
            return this.vuexModule.unitOfMeasures[family_name];
        }
        return this.vuexModule.unitOfMeasures[family_name];
    }

    public async updateCompentences(pkCuaa: number) {
        const [readProfile, editProfile ] = await Promise.all([
            this.TreatmentApi.getUserPermissions(pkCuaa, "BRG", "VIEW"),
            this.TreatmentApi.getUserPermissions(pkCuaa, "BRG", "EDIT")
        ])
        this.vuexModule.readProfile = readProfile;
        this.vuexModule.editProfile = editProfile;
    }

}
